import { API_BASE_URL } from '../config';
// src/components/InventoryDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaBarcode,
  FaFileImport,
  FaSearch,
  FaTimes,
  FaArrowUp,
  FaShoppingCart,
  FaHammer,
  FaBoxes,
  FaExclamationTriangle,
  FaSync,
  FaTruck,
  FaClipboardList,
  FaCheckCircle,
  FaTimesCircle,
  FaCheck
} from 'react-icons/fa';
import Modal from './common/Modal';
import ProductForm from './ProductForm';
import ExcelImport from './ExcelImport';
import BarcodeModal from './BarcodeModal';

const InventoryDashboard = ({ onRecordPurchase, onAddProduct, onOpenBOM, suppliers, onDataChange, products: parentProducts }) => {
  const [products, setProducts] = useState(parentProducts || []);
  const [loading, setLoading] = useState(!parentProducts || parentProducts.length === 0);
  const [error, setError] = useState(null);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterLowStockOnly, setFilterLowStockOnly] = useState(false);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);

  // SMS Sync state
  const [syncingSMS, setSyncingSMS] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  // SMS Agent Allotted Stock Inspector state
  const [showAgentStockModal, setShowAgentStockModal] = useState(false);
  const [agentStockSummary, setAgentStockSummary] = useState(null);
  const [loadingAgentStock, setLoadingAgentStock] = useState(false);
  const [agentStockViewTab, setAgentStockViewTab] = useState('byAgent'); // 'byAgent' | 'byProduct'
  const [agentSearchFilter, setAgentSearchFilter] = useState('');

  // SMS Agent Indents Management state
  const [showIndentsModal, setShowIndentsModal] = useState(false);
  const [indentsList, setIndentsList] = useState([]);
  const [loadingIndents, setLoadingIndents] = useState(false);
  const [pendingIndentsCount, setPendingIndentsCount] = useState(0);
  const [indentFilter, setIndentFilter] = useState('REQUESTED'); // 'REQUESTED' | 'DISPATCHED' | 'ALL'
  const [actionInProgressId, setActionInProgressId] = useState(null);
  const [indentSearchTerm, setIndentSearchTerm] = useState('');

  const fetchAgentStock = async () => {
    setLoadingAgentStock(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/server/api/sync_sms.php?action=agent_inventory`);
      if (res.data && res.data.success) {
        setAgentStockSummary(res.data);
      }
    } catch (err) {
      console.error('Failed to load agent inventory:', err);
    } finally {
      setLoadingAgentStock(false);
    }
  };

  const openAgentInventoryModal = () => {
    setShowAgentStockModal(true);
    fetchAgentStock();
  };

  const fetchIndents = async () => {
    setLoadingIndents(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/server/api/sync_sms.php?action=all_indents`);
      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        setIndentsList(res.data.data);
        const pending = res.data.data.filter(i => i.status === 'REQUESTED').length;
        setPendingIndentsCount(pending);
      }
    } catch (err) {
      console.error('Failed to load agent indents:', err);
    } finally {
      setLoadingIndents(false);
    }
  };

  const openIndentsModal = () => {
    setShowIndentsModal(true);
    fetchIndents();
  };

  const handleDispatchIndent = async (indentId, remarks = '') => {
    if (!window.confirm('Are you sure you want to approve & dispatch these parts to the agent? This will deduct the parts from central POS stock and add them to the agent van kit.')) return;
    setActionInProgressId(indentId);
    try {
      const res = await axios.post(`${API_BASE_URL}/server/api/sync_sms.php?action=dispatch_indent`, {
        indent_id: indentId,
        remarks: remarks || 'Dispatched via Central POS Console'
      });
      if (res.data && (res.data.success || res.data.status === 'success')) {
        alert('Indent approved & dispatched successfully! Central stock updated.');
        await fetchIndents();
        if (onDataChange) onDataChange();
        fetchProducts();
      } else {
        alert(res.data?.error || res.data?.message || 'Failed to dispatch indent.');
      }
    } catch (err) {
      console.error('Dispatch indent error:', err);
      alert('Error dispatching indent: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionInProgressId(null);
    }
  };

  const handleRejectIndent = async (indentId) => {
    const reason = window.prompt('Enter reason for rejecting this indent requisition:');
    if (reason === null) return;
    setActionInProgressId(indentId);
    try {
      const res = await axios.post(`${API_BASE_URL}/server/api/sync_sms.php?action=reject_indent`, {
        indent_id: indentId,
        remarks: reason || 'Rejected by Central POS Store Manager'
      });
      if (res.data && (res.data.success || res.data.status === 'success')) {
        alert('Indent requisition marked as rejected.');
        await fetchIndents();
      } else {
        alert(res.data?.error || res.data?.message || 'Failed to reject indent.');
      }
    } catch (err) {
      console.error('Reject indent error:', err);
      alert('Error rejecting indent: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionInProgressId(null);
    }
  };

  const handleSyncWithSMS = async () => {
    try {
      setSyncingSMS(true);
      setSyncStatus(null);
      const res = await axios.post(`${API_BASE_URL}/server/api/sync_sms.php?action=push_all`);
      if (res.data && res.data.status === 'success') {
        setSyncStatus({ type: 'success', message: res.data.message });
      } else {
        setSyncStatus({ type: 'warning', message: res.data.message || 'Sync response received' });
      }
    } catch (err) {
      console.error('SMS Sync Error:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to sync with SMS Backend';
      setSyncStatus({ type: 'error', message: msg });
    } finally {
      setSyncingSMS(false);
      setTimeout(() => setSyncStatus(null), 6000);
    }
  };

  // floating search (mobile)
  const [floatingSearchOpen, setFloatingSearchOpen] = useState(false);
  const floatingInputRef = useRef(null);

  // go-to-top
  const [showGoTop, setShowGoTop] = useState(false);
  const [goTopBottom, setGoTopBottom] = useState(96); // px; default above mobile navbar

  // bulk-edit modal state (desktop)
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEdits, setBulkEdits] = useState({
    category: '',
    price: '',
    stockDelta: 0,
    stockMode: 'delta' // 'delta' or 'set'
  });
  const [bulkUpdating, setBulkUpdating] = useState(false);

  useEffect(() => {
    if (parentProducts && Array.isArray(parentProducts)) {
      setProducts(parentProducts);
      setLoading(false);
    } else {
      fetchProducts();
    }
    fetchIndents();
  }, [parentProducts]);

  useEffect(() => {
    // update position for go-to-top based on window width (desktop lower, mobile higher)
    const updateBottom = () => {
      try {
        const w = window.innerWidth || document.documentElement.clientWidth || 1024;
        if (w < 768) {
          // mobile: place above bottom navbar
          setGoTopBottom(96);
        } else {
          // desktop: small offset
          setGoTopBottom(24);
        }
      } catch {
        setGoTopBottom(96);
      }
    };
    updateBottom();
    window.addEventListener('resize', updateBottom);
    return () => window.removeEventListener('resize', updateBottom);
  }, []);

  useEffect(() => {
    // robust scroll detection: listen to window + likely scroll containers
    const handler = () => {
      const y = (window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0);
      setShowGoTop(y > 200);
    };

    // helper to add/remove to element if it supports addEventListener
    const add = (el) => {
      try {
        if (!el) return;
        el.addEventListener('scroll', handler, { passive: true });
      } catch (e) {}
    };
    const remove = (el) => {
      try {
        if (!el) return;
        el.removeEventListener('scroll', handler);
      } catch (e) {}
    };

    // common candidates for scroll containers
    const candidates = [
      window,
      document,
      document.scrollingElement,
      document.documentElement,
      document.body,
      document.getElementById('root'),
      document.querySelector('main'),
      document.querySelector('.flex-1'),
      document.querySelector('.app-main'),
      document.querySelector('.app-scroll'),
      document.querySelector('[role="main"]')
    ];

    const attached = [];
    candidates.forEach((c) => {
      if (c && typeof c.addEventListener === 'function') {
        add(c);
        attached.push(c);
      }
    });

    // trigger initial check
    handler();

    return () => {
      attached.forEach(remove);
    };
  }, []);

  useEffect(() => {
    if (floatingSearchOpen && floatingInputRef.current) {
      setTimeout(() => floatingInputRef.current.focus(), 80);
    }
  }, [floatingSearchOpen]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
       //const response = await axios.get(API_BASE_URL + '/server/api/products.php');
      const response = await axios.get(`${API_BASE_URL}/server/api/products.php`);
      const data = response && response.data;
      if (Array.isArray(data)) setProducts(data);
      else if (data && Array.isArray(data.data)) setProducts(data.data);
      else if (data && Array.isArray(data.products)) setProducts(data.products);
      else if (data && typeof data === 'object' && Object.keys(data).length > 0) setProducts([data]);
      else setProducts([]);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch products. Please check the backend server.');
      setLoading(false);
    }
  };

  const handleEditProduct = (product) => {
    setCurrentProduct(product);
    setIsFormModalOpen(true);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      //await axios.delete(`${API_BASE_URL}/server/api/products.php?id=${id}`);
      await axios.delete(`${API_BASE_URL}/server/api/products.php?id=${id}`);
      fetchProducts();
      setSelectedProducts(prev => prev.filter(x => x !== id));
    } catch (err) {
      console.error('Error deleting product:', err);
      alert('Failed to delete product.');
    }
  };

  const handleProductSelection = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) setSelectedProducts(products.map((p) => p.id));
    else setSelectedProducts([]);
  };

  const handleBulkBarcode = () => {
    if (selectedProducts.length === 0) return;
    setShowBarcodeModal(true);
  };

  const isLowStock = (product) => {
    const cur = Number(product.stock_level ?? product.stock ?? 0);
    const min = Number(product.min_stock_level ?? 0);
    return (min > 0 && cur <= min) || cur <= 0;
  };

  const lowStockCount = products.filter(isLowStock).length;

  const normalizedFilter = (product, term) => {
    if (filterLowStockOnly && !isLowStock(product)) return false;
    const name = product && product.name ? String(product.name).toLowerCase() : '';
    const sku = product && product.sku ? String(product.sku).toLowerCase() : '';
    const cat = product && product.category ? String(product.category).toLowerCase() : '';
    const termLow = term.trim().toLowerCase();
    if (!termLow) return true;
    return name.includes(termLow) || sku.includes(termLow) || cat.includes(termLow);
  };

  const filteredProducts = products.filter((p) => normalizedFilter(p, searchTerm));
  const barcodesToPrint = products.filter((p) => selectedProducts.includes(p.id));

  const scrollToTop = () => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      try {
        const el = document.querySelector('main') || document.getElementById('root') || document.body;
        if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {}
    }
  };

  const openBulkEdit = () => {
    if (selectedProducts.length === 0) return;
    setBulkEdits({
      category: '',
      price: '',
      stockDelta: 0,
      stockMode: 'delta'
    });
    setBulkEditOpen(true);
  };

  const submitBulkEdit = async () => {
    if (selectedProducts.length === 0) return;
    setBulkUpdating(true);

    const errors = [];
    for (const id of selectedProducts) {
      try {
        const updateObj = {};
        if (bulkEdits.category) updateObj.category = bulkEdits.category;
        if (bulkEdits.price !== '') {
          const priceValue = Number(bulkEdits.price);
          if (!Number.isNaN(priceValue)) updateObj.price = priceValue;
        }
        if (bulkEdits.stockMode === 'set') {
          updateObj.stock = Number(bulkEdits.stockDelta) || 0;
        } else if (bulkEdits.stockMode === 'delta') {
          updateObj.stock_delta = Number(bulkEdits.stockDelta) || 0;
        }
        await axios.put(`${API_BASE_URL}/server/api/products.php?id=${id}`, updateObj, {
          headers: { 'Content-Type': 'application/json' }
        });
      } catch (err) {
        console.error('Bulk update error for id', id, err);
        errors.push(id);
      }
    }

    setBulkUpdating(false);
    setBulkEditOpen(false);
    await fetchProducts();
    if (errors.length > 0) alert(`Bulk update completed with errors for ${errors.length} items.`);
    else setSelectedProducts([]);
  };

  if (loading) return <div className="text-center p-4">Loading products...</div>;
  if (error) return <div className="text-center p-4 text-red-600">{error}</div>;

  return (
    <div className="w-full">
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
        
        {/* Mobile: Stacked Action Buttons */}
        <div className="flex flex-col space-y-2 md:hidden mb-4">
            <button
                onClick={handleSyncWithSMS}
                disabled={syncingSMS}
                className="w-full flex items-center justify-center px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg shadow-md font-semibold transition-colors disabled:opacity-60"
            >
                <FaSync className={`mr-2 ${syncingSMS ? 'animate-spin' : ''}`} />
                {syncingSMS ? 'Syncing with SMS...' : 'Sync Catalog to SMS'}
            </button>
            <button onClick={openIndentsModal} className="w-full flex items-center justify-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg shadow-md font-semibold transition-colors">
                <FaClipboardList className="mr-2" /> Agent Indents
                {pendingIndentsCount > 0 && (
                  <span className="ml-2 bg-white text-amber-900 px-2 py-0.5 rounded-full text-xs font-black">
                    {pendingIndentsCount} PENDING
                  </span>
                )}
            </button>
            <button onClick={openAgentInventoryModal} className="w-full flex items-center justify-center px-4 py-2 bg-indigo-800 text-white rounded-lg shadow-md font-semibold">
                <FaTruck className="mr-2" /> Agent Van Kits
            </button>
            <button onClick={onOpenBOM} className="w-full flex items-center justify-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md font-semibold">
                <FaHammer className="mr-2" /> Packing List
            </button>
            <button onClick={onRecordPurchase} className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg shadow-md">
                <FaShoppingCart className="mr-2" /> Record New Purchase
            </button>
            <button onClick={() => setShowBulkImportModal(true)} className="w-full flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg shadow-md">
                <FaFileImport className="mr-2" /> Bulk Import
            </button>
            <button onClick={handleBulkBarcode} disabled={selectedProducts.length === 0} className={`w-full flex items-center justify-center px-4 py-2 rounded-lg shadow-md ${selectedProducts.length === 0 ? 'bg-gray-200 text-gray-600 cursor-not-allowed' : 'bg-indigo-600 text-white'}`}>
                <FaBarcode className="mr-2" /> Bulk Generate Barcodes
            </button>
            <button onClick={onAddProduct} className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md">
                <FaPlus className="mr-2" /> Add New Product
            </button>
        </div>
        
        {/* Desktop: Horizontal Action Buttons */}
        <div className="hidden md:flex md:flex-row md:items-center md:justify-between mb-4 gap-2">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Inventory Management</h2>
              {lowStockCount > 0 && (
                <span className="inline-flex items-center space-x-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-0.5 rounded-full mt-1">
                  <FaExclamationTriangle className="text-amber-500" />
                  <span>{lowStockCount} product(s) at or below minimum inventory</span>
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
                <button
                    onClick={handleSyncWithSMS}
                    disabled={syncingSMS}
                    className="flex items-center px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg shadow-md font-semibold transition-colors disabled:opacity-60"
                    title="Force full product catalog & price sync to SBR SMS"
                >
                    <FaSync className={`mr-2 ${syncingSMS ? 'animate-spin' : ''}`} />
                    {syncingSMS ? 'Syncing...' : 'Sync with SMS'}
                </button>
                <button
                    onClick={openIndentsModal}
                    className="flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg shadow-md font-semibold transition-colors relative"
                    title="Review & approve parts requisitions submitted by SMS field agents"
                >
                    <FaClipboardList className="mr-2" /> Agent Indents
                    {pendingIndentsCount > 0 && (
                      <span className="ml-2 bg-white text-amber-900 px-2 py-0.5 rounded-full text-xs font-black animate-pulse">
                        {pendingIndentsCount} PENDING
                      </span>
                    )}
                </button>
                <button
                    onClick={openAgentInventoryModal}
                    className="flex items-center px-4 py-2 bg-indigo-800 hover:bg-indigo-900 text-white rounded-lg shadow-md font-semibold transition-colors"
                    title="View live stock allocated to SMS field agents"
                >
                    <FaTruck className="mr-2" /> Agent Van Kits
                </button>
                <button onClick={onOpenBOM} className="flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all">
                    <FaBoxes className="mr-2" /> Packing List
                </button>
                <button onClick={onRecordPurchase} className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700 transition-colors">
                    <FaShoppingCart className="mr-2" /> Record Purchase
                </button>
                <button onClick={() => setShowBulkImportModal(true)} className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg shadow-md hover:bg-purple-700 transition-colors">
                    <FaFileImport className="mr-2" /> Bulk Import
                </button>
                <button onClick={handleBulkBarcode} disabled={selectedProducts.length === 0} className={`flex items-center px-4 py-2 rounded-lg shadow-md ${selectedProducts.length === 0 ? 'bg-gray-200 text-gray-600 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                    <FaBarcode className="mr-2" /> Barcodes
                </button>
                <button onClick={onAddProduct} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-colors">
                    <FaPlus className="mr-2" /> Add Product
                </button>
            </div>
        </div>

        {/* Sync Status Feedback Toast/Banner */}
        {syncStatus && (
          <div className={`mb-4 p-3 rounded-xl border flex items-center justify-between text-sm font-medium ${
            syncStatus.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : syncStatus.type === 'warning'
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <div className="flex items-center space-x-2">
              <FaSync className={syncStatus.type === 'success' ? 'text-green-600' : 'text-amber-600'} />
              <span>{syncStatus.message}</span>
            </div>
            <button onClick={() => setSyncStatus(null)} className="text-gray-400 hover:text-gray-600">
              <FaTimes />
            </button>
          </div>
        )}
        
        {/* Search, Filter & Quick Toggles */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="relative flex-1">
                <input
                    type="text"
                    placeholder="Search products by name, SKU, category..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <button
                onClick={() => setFilterLowStockOnly(!filterLowStockOnly)}
                className={`flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  filterLowStockOnly
                    ? 'bg-amber-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border'
                }`}
            >
                <FaExclamationTriangle className="mr-2 text-xs" />
                <span>Low Stock ({lowStockCount})</span>
            </button>
        </div>

        <div className="flex justify-between items-center mb-4">
            <label className="flex items-center space-x-2 text-sm text-gray-700">
                <input
                    type="checkbox"
                    className="w-4 h-4 rounded-full border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    checked={products.length > 0 && selectedProducts.length === products.length}
                />
                <span className="text-xs sm:text-sm">Select all</span>
            </label>
            {/* Desktop bulk edit button (visible on large screens) */}
            <button
                onClick={openBulkEdit}
                disabled={selectedProducts.length === 0}
                className={`hidden md:inline-flex items-center px-4 py-2 rounded-lg shadow-md transition-colors text-sm ${
                    selectedProducts.length === 0 ? 'bg-gray-200 text-gray-600 cursor-not-allowed' : 'bg-yellow-600 text-white hover:bg-yellow-700'
                }`}
                title="Bulk edit selected rows"
            >
                Bulk Edit ({selectedProducts.length})
            </button>
        </div>

        {/* Desktop table view */}
        <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="p-3 text-center" />
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock (Min Threshold)</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {filteredProducts.map((product) => {
                        const low = isLowStock(product);
                        const curStock = Number(product.stock_level ?? product.stock ?? 0);
                        const minStock = Number(product.min_stock_level ?? 0);

                        return (
                            <tr key={product.id} className={low ? 'bg-amber-50/50' : ''}>
                                <td className="p-3 text-center">
                                    <input type="checkbox" className="w-4 h-4 rounded-full" checked={selectedProducts.includes(product.id)} onChange={() => handleProductSelection(product.id)} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.sku || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.category || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">₹{Number(product.price || 0).toFixed(2)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <div className="flex items-center space-x-2">
                                        <span className={`font-bold ${low ? 'text-red-600' : 'text-gray-900'}`}>
                                            {curStock}
                                        </span>
                                        {minStock > 0 && (
                                            <span className="text-xs text-gray-500">
                                                (Min: {minStock})
                                            </span>
                                        )}
                                        {low && (
                                            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700 flex items-center space-x-1">
                                                <FaExclamationTriangle className="text-[10px]" />
                                                <span>Low</span>
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.supplier_name || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                    <button onClick={() => handleEditProduct(product)} className="text-indigo-600 hover:text-indigo-800" title="Edit Product">
                                        <FaEdit />
                                    </button>
                                    <button onClick={() => handleDeleteProduct(product.id)} className="text-red-600 hover:text-red-800" title="Delete Product">
                                        <FaTrash />
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {filteredProducts.length === 0 && <div className="text-center py-6 text-gray-500">No products found.</div>}
        </div>

        {/* Mobile card view */}
        <div className="md:hidden space-y-4">
            {filteredProducts.length === 0 && <div className="text-center py-6 text-gray-500">No products found.</div>}
            {filteredProducts.map((product) => {
                const low = isLowStock(product);
                const curStock = Number(product.stock_level ?? product.stock ?? 0);
                const minStock = Number(product.min_stock_level ?? 0);

                return (
                    <div key={product.id} className={`bg-white p-4 rounded-lg shadow-md border-t-4 ${low ? 'border-red-500 bg-red-50/20' : 'border-blue-600'}`}>
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="font-semibold text-lg">{product.name || '-'}</h3>
                                <p className="text-sm text-gray-600">SKU: {product.sku || '-'}</p>
                            </div>
                            <div className="flex space-x-2">
                                <button onClick={() => handleEditProduct(product)} className="text-blue-600">
                                    <FaEdit />
                                </button>
                                <button onClick={() => handleDeleteProduct(product.id)} className="text-red-600">
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                        <div className="mt-2 text-sm">
                            <p><span className="font-semibold">Category:</span> {product.category || '-'}</p>
                            <p><span className="font-semibold">Price:</span> ₹{Number(product.price || 0).toFixed(2)}</p>
                            <div className="flex items-center space-x-2 mt-1">
                                <span className="font-semibold">Stock:</span>
                                <span className={`font-bold ${low ? 'text-red-600' : 'text-gray-900'}`}>{curStock}</span>
                                {minStock > 0 && <span className="text-xs text-gray-500">(Min: {minStock})</span>}
                                {low && (
                                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                                        Low Stock
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-between items-center mt-3 border-t pt-3">
                            <label className="flex items-center space-x-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded"
                                    checked={selectedProducts.includes(product.id)}
                                    onChange={() => handleProductSelection(product.id)}
                                />
                                <span>Select</span>
                            </label>
                            <button
                                onClick={() => {
                                    if (!selectedProducts.includes(product.id)) handleProductSelection(product.id);
                                    setShowBarcodeModal(true);
                                }}
                                className="bg-indigo-600 text-white px-3 py-1 rounded-lg text-xs flex items-center space-x-1"
                            >
                                <FaBarcode />
                                <span>Barcode</span>
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
      </div>

      {/* Modals */}
      {isFormModalOpen && (
        <Modal onClose={() => {
          setIsFormModalOpen(false);
          setCurrentProduct(null);
        }}>
          <ProductForm
            initialData={currentProduct}
            onProductAdded={() => {
              setIsFormModalOpen(false);
              setCurrentProduct(null);
              fetchProducts();
              if (onDataChange) onDataChange();
            }}
          />
        </Modal>
      )}

      {showBarcodeModal && (
        <BarcodeModal
          barcodes={barcodesToPrint.map(p => ({ sku: p.sku, name: p.name }))}
          onClose={() => setShowBarcodeModal(false)}
          mode="print"
        />
      )}

      {showBulkImportModal && (
        <Modal onClose={() => setShowBulkImportModal(false)}>
          <ExcelImport
            type="products"
            title="Import Products / Inventory (Excel / CSV)"
            onImportComplete={() => {
              if (onDataChange) onDataChange();
            }}
          />
        </Modal>
      )}

      {bulkEditOpen && (
        <Modal onClose={() => setBulkEditOpen(false)}>
          <div className="max-w-md w-full">
            <h3 className="text-lg font-semibold mb-3">Bulk Edit ({selectedProducts.length} items)</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Category (set)</label>
                <input type="text" value={bulkEdits.category} onChange={(e) => setBulkEdits((s) => ({ ...s, category: e.target.value }))} className="mt-1 block w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Price (set)</label>
                <input type="number" value={bulkEdits.price} onChange={(e) => setBulkEdits((s) => ({ ...s, price: e.target.value }))} className="mt-1 block w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Stock</label>
                <div className="mt-1 flex gap-2">
                  <select value={bulkEdits.stockMode} onChange={(e) => setBulkEdits((s) => ({ ...s, stockMode: e.target.value }))} className="border rounded px-3 py-2">
                    <option value="delta">Add (delta)</option>
                    <option value="set">Set absolute</option>
                  </select>
                  <input type="number" value={bulkEdits.stockDelta} onChange={(e) => setBulkEdits((s) => ({ ...s, stockDelta: e.target.value }))} className="flex-1 border rounded px-3 py-2" />
                </div>
                <p className="text-xs text-gray-500 mt-1">If you choose "Add (delta)" enter +5 or -3 to increment or decrement stock.</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end space-x-2">
              <button onClick={() => setBulkEditOpen(false)} className="px-4 py-2 rounded bg-gray-100">Cancel</button>
              <button onClick={submitBulkEdit} disabled={bulkUpdating} className="px-4 py-2 rounded bg-yellow-600 text-white">
                {bulkUpdating ? 'Updating...' : 'Apply to selected'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* SMS Field Agent Allotted Stock Modal */}
      {showAgentStockModal && (
        <Modal onClose={() => setShowAgentStockModal(false)} maxWidth="max-w-5xl">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b gap-3">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-gradient-to-br from-indigo-600 to-blue-700 rounded-xl text-white shadow-md">
                  <FaTruck className="text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">SMS Field Agent Allotted Stock (Van Kits)</h3>
                  <p className="text-xs text-gray-500">Live spare parts and kits in transit / with field agents from SMS application</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={fetchAgentStock}
                  disabled={loadingAgentStock}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold flex items-center space-x-1"
                >
                  <FaSync className={loadingAgentStock ? 'animate-spin' : ''} />
                  <span>Refresh</span>
                </button>
                <button onClick={() => setShowAgentStockModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Summary KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-4">
              <div className="bg-indigo-50 border border-indigo-100 p-3.5 rounded-xl">
                <span className="text-xs font-semibold text-indigo-700 block uppercase tracking-wider">Total Units in Field</span>
                <span className="text-2xl font-black text-indigo-900 mt-1 block">
                  {agentStockSummary?.totalItemsAllotted ?? 0}
                </span>
                <span className="text-[11px] text-indigo-600">Spare parts in active van kits</span>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 p-3.5 rounded-xl">
                <span className="text-xs font-semibold text-emerald-700 block uppercase tracking-wider">Active Agents Carrying Stock</span>
                <span className="text-2xl font-black text-emerald-900 mt-1 block">
                  {agentStockSummary?.totalAgentsWithStock ?? 0}
                </span>
                <span className="text-[11px] text-emerald-600">Field service technicians</span>
              </div>
              <div className="bg-purple-50 border border-purple-100 p-3.5 rounded-xl">
                <span className="text-xs font-semibold text-purple-700 block uppercase tracking-wider">Unique Spare Parts</span>
                <span className="text-2xl font-black text-purple-900 mt-1 block">
                  {agentStockSummary?.byProduct?.length ?? 0}
                </span>
                <span className="text-[11px] text-purple-600">Catalog SKUs deployed</span>
              </div>
            </div>

            {/* Tabs & Search */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2 mb-3 border-b pb-2">
              <div className="flex space-x-2">
                <button
                  onClick={() => setAgentStockViewTab('byAgent')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    agentStockViewTab === 'byAgent'
                      ? 'bg-indigo-600 text-white shadow'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  By Field Agent ({agentStockSummary?.byAgent?.length ?? 0})
                </button>
                <button
                  onClick={() => setAgentStockViewTab('byProduct')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    agentStockViewTab === 'byProduct'
                      ? 'bg-indigo-600 text-white shadow'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  By Product / Part ({agentStockSummary?.byProduct?.length ?? 0})
                </button>
              </div>

              <div className="relative w-full sm:w-56">
                <input
                  type="text"
                  placeholder="Filter agents or parts..."
                  value={agentSearchFilter}
                  onChange={(e) => setAgentSearchFilter(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 border rounded-lg text-xs focus:ring-2 focus:ring-indigo-500"
                />
                <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              </div>
            </div>

            {/* Content List */}
            {loadingAgentStock ? (
              <div className="py-12 text-center text-gray-500">Loading Agent Stock from SMS...</div>
            ) : agentStockViewTab === 'byAgent' ? (
              <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                {(!agentStockSummary?.byAgent || agentStockSummary.byAgent.length === 0) ? (
                  <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-xl border border-dashed">
                    No field agents currently have stock allocated in their Van Kits.
                  </div>
                ) : (
                  agentStockSummary.byAgent
                    .filter(a => !agentSearchFilter || a.name.toLowerCase().includes(agentSearchFilter.toLowerCase()) || (a.phone && a.phone.includes(agentSearchFilter)))
                    .map(agent => (
                      <div key={agent.agentId} className="border rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start pb-2.5 border-b">
                          <div className="flex items-center space-x-2.5">
                            <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-sm">
                              {agent.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h4 className="font-bold text-gray-900 text-sm">{agent.name}</h4>
                              <div className="text-xs text-gray-500 space-x-2">
                                {agent.phone && <span>📞 {agent.phone}</span>}
                                {agent.email && <span>✉ {agent.email}</span>}
                              </div>
                            </div>
                          </div>
                          <span className="px-3 py-1 bg-indigo-50 text-indigo-800 text-xs font-bold rounded-full border border-indigo-200">
                            {agent.totalQuantity} Units in Van
                          </span>
                        </div>

                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                          {agent.items.map((item, i) => (
                            <div key={i} className="flex justify-between items-center bg-gray-50 p-2 rounded-lg text-xs border border-gray-100">
                              <div className="truncate mr-2">
                                <span className="font-medium text-gray-900 block truncate">{item.productName}</span>
                                {item.sku && <span className="text-[10px] text-gray-500 block">SKU: {item.sku}</span>}
                              </div>
                              <span className="font-bold text-indigo-700 bg-white px-2 py-0.5 rounded shadow-sm border whitespace-nowrap">
                                {item.quantity} pcs
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                )}
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-xl shadow-sm max-h-[55vh]">
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600 uppercase">Product / Part Name</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600 uppercase">SKU</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600 uppercase">Total in Field</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600 uppercase">Agents Carrying</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {(!agentStockSummary?.byProduct || agentStockSummary.byProduct.length === 0) ? (
                      <tr>
                        <td colSpan="4" className="text-center py-6 text-gray-500">No products deployed with agents yet.</td>
                      </tr>
                    ) : (
                      agentStockSummary.byProduct
                        .filter(p => !agentSearchFilter || p.productName.toLowerCase().includes(agentSearchFilter.toLowerCase()))
                        .map((p, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 font-bold text-gray-900">{p.productName}</td>
                            <td className="px-4 py-2.5 text-gray-500">{p.sku || '-'}</td>
                            <td className="px-4 py-2.5 text-right font-bold text-indigo-700 text-sm">{p.totalAllotted} units</td>
                            <td className="px-4 py-2.5 text-gray-600">
                              {p.agents.map((a, i) => (
                                <span key={i} className="inline-block bg-gray-100 rounded px-2 py-0.5 mr-1 mb-1 text-[11px]">
                                  {a.agentName}: <strong>{a.quantity}</strong>
                                </span>
                              ))}
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* SMS Agent Indent Requisitions Modal */}
      {showIndentsModal && (
        <Modal onClose={() => setShowIndentsModal(false)} maxWidth="max-w-5xl">
          <div className="p-4 md:p-6 space-y-4">
            
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b gap-2">
              <div>
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <FaClipboardList className="text-amber-600" /> Field Agent Indent Requisitions
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Approve parts requests from SMS field agents and directly issue inventory from Central POS warehouse to Agent Van Kits.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchIndents}
                  disabled={loadingIndents}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
                >
                  <FaSync className={`text-xs ${loadingIndents ? 'animate-spin' : ''}`} /> Refresh
                </button>
              </div>
            </div>

            {/* Filter Tabs & Search */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
              <div className="flex items-center gap-1.5 bg-gray-100 p-1 rounded-xl">
                <button
                  onClick={() => setIndentFilter('REQUESTED')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    indentFilter === 'REQUESTED'
                      ? 'bg-amber-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Pending Approval ({indentsList.filter(i => i.status === 'REQUESTED').length})
                </button>
                <button
                  onClick={() => setIndentFilter('DISPATCHED')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    indentFilter === 'DISPATCHED'
                      ? 'bg-green-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Dispatched ({indentsList.filter(i => i.status === 'DISPATCHED').length})
                </button>
                <button
                  onClick={() => setIndentFilter('ALL')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    indentFilter === 'ALL'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All History ({indentsList.length})
                </button>
              </div>

              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  placeholder="Search agent, service or parts..."
                  value={indentSearchTerm}
                  onChange={(e) => setIndentSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 border rounded-lg text-xs focus:ring-2 focus:ring-amber-500"
                />
                <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              </div>
            </div>

            {/* Indent Cards List */}
            {loadingIndents ? (
              <div className="py-16 text-center text-gray-500">
                <FaSync className="animate-spin text-2xl mx-auto mb-2 text-amber-600" />
                Loading requisitions from SMS...
              </div>
            ) : (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                {(() => {
                  const filtered = indentsList
                    .filter(ind => {
                      if (indentFilter !== 'ALL' && ind.status !== indentFilter) return false;
                      if (!indentSearchTerm) return true;
                      const term = indentSearchTerm.toLowerCase();
                      const aName = ind.agentId?.name?.toLowerCase() || '';
                      const aPhone = ind.agentId?.phone || '';
                      const sType = ind.serviceRequestId?.serviceType?.toLowerCase() || '';
                      const pNames = (ind.items || []).map(i => i.productName?.toLowerCase()).join(' ');
                      return aName.includes(term) || aPhone.includes(term) || sType.includes(term) || pNames.includes(term);
                    });

                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-2xl border border-dashed">
                        <FaClipboardList className="text-3xl text-gray-300 mx-auto mb-2" />
                        <p className="font-semibold text-sm text-gray-700">No indent requisitions found in this view</p>
                        <p className="text-xs text-gray-500 mt-0.5">When SMS agents request parts for services or van stock, they will appear here for dispatch.</p>
                      </div>
                    );
                  }

                  return filtered.map(indent => {
                    const isPending = indent.status === 'REQUESTED';
                    const isDispatched = indent.status === 'DISPATCHED';
                    const isRejected = indent.status === 'REJECTED';
                    const isWorking = actionInProgressId === indent._id;

                    return (
                      <div
                        key={indent._id}
                        className={`border rounded-2xl p-5 bg-white shadow-sm hover:shadow-md transition-all ${
                          isPending ? 'border-amber-300 bg-amber-50/20' : 'border-gray-200'
                        }`}
                      >
                        {/* Top Meta Bar */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b gap-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-800 font-bold flex items-center justify-center text-sm">
                              {indent.agentId?.name?.charAt(0).toUpperCase() || 'A'}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-bold text-gray-900 text-sm">{indent.agentId?.name || 'Field Agent'}</h4>
                                {indent.agentId?.phone && (
                                  <span className="text-xs text-gray-500 font-mono">📞 {indent.agentId.phone}</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 flex items-center gap-3 mt-0.5">
                                <span>Requested: {new Date(indent.requestedAt || indent.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                {indent.serviceRequestId && (
                                  <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded font-semibold text-[11px] border border-indigo-200">
                                    For Job: {indent.serviceRequestId.serviceType}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Status Badge */}
                          <div>
                            {isPending ? (
                              <span className="inline-flex items-center gap-1 text-xs font-bold bg-amber-100 text-amber-800 px-3 py-1 rounded-full border border-amber-300">
                                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                                Awaiting Store Dispatch
                              </span>
                            ) : isDispatched ? (
                              <span className="inline-flex items-center gap-1 text-xs font-bold bg-green-100 text-green-800 px-3 py-1 rounded-full border border-green-300">
                                <FaCheck className="text-green-600 text-xs" />
                                Dispatched to Van
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-bold bg-red-100 text-red-800 px-3 py-1 rounded-full border border-red-300">
                                <FaTimes className="text-red-600 text-xs" />
                                Rejected
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Agent Note */}
                        {indent.agentRemarks && (
                          <div className="mt-3 p-2.5 bg-gray-50 rounded-xl text-xs text-gray-700 italic border border-gray-100">
                            <strong>Agent Remarks:</strong> "{indent.agentRemarks}"
                          </div>
                        )}

                        {/* Store Incharge Note */}
                        {indent.inchargeRemarks && (
                          <div className="mt-2 p-2 bg-emerald-50 text-emerald-800 rounded-lg text-xs border border-emerald-200">
                            <strong>Store Notes:</strong> {indent.inchargeRemarks}
                          </div>
                        )}

                        {/* Requested Parts Table */}
                        <div className="mt-4 border rounded-xl overflow-hidden shadow-xs">
                          <table className="min-w-full divide-y divide-gray-200 text-xs">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600 uppercase">Part / Product Name</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-600 uppercase">SKU</th>
                                <th className="px-3 py-2 text-center font-semibold text-gray-600 uppercase">Requested Qty</th>
                                <th className="px-3 py-2 text-center font-semibold text-gray-600 uppercase">Central POS Stock</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {(indent.items || []).map((it, idx) => {
                                // Match with central POS products by posProductId or name
                                const matchedPosProd = products.find(p => 
                                  (it.posProductId && Number(p.id) === Number(it.posProductId)) ||
                                  (p.name && p.name.trim().toLowerCase() === it.productName.trim().toLowerCase())
                                );
                                const curStock = matchedPosProd ? Number(matchedPosProd.stock_level ?? matchedPosProd.stock ?? 0) : null;
                                const hasSufficient = curStock !== null && curStock >= it.requestedQuantity;

                                return (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 font-bold text-gray-900">{it.productName}</td>
                                    <td className="px-3 py-2 text-gray-500 font-mono">{it.sku || matchedPosProd?.sku || '-'}</td>
                                    <td className="px-3 py-2 text-center font-black text-amber-700 text-sm">
                                      {it.dispatchedQuantity > 0 ? `${it.dispatchedQuantity} / ${it.requestedQuantity}` : it.requestedQuantity}
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      {curStock === null ? (
                                        <span className="text-gray-400 font-medium">Unlinked Item</span>
                                      ) : hasSufficient ? (
                                        <span className="inline-flex items-center gap-1 font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200">
                                          <FaCheckCircle className="text-xs" /> {curStock} in Store
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded border border-red-200">
                                          <FaExclamationTriangle className="text-xs" /> {curStock} Low/Short
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Action Buttons for Pending Requisition */}
                        {isPending && (
                          <div className="mt-4 pt-3 border-t flex items-center justify-end gap-2.5">
                            <button
                              onClick={() => handleRejectIndent(indent._id)}
                              disabled={isWorking}
                              className="px-4 py-2 bg-gray-100 hover:bg-red-50 text-gray-700 hover:text-red-700 border rounded-xl text-xs font-bold transition-all disabled:opacity-50"
                            >
                              Reject
                            </button>
                            <button
                              onClick={() => handleDispatchIndent(indent._id)}
                              disabled={isWorking}
                              className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white rounded-xl text-xs font-bold shadow-md transition-all flex items-center gap-1.5 disabled:opacity-50"
                            >
                              <FaCheckCircle className="text-xs" />
                              {isWorking ? 'Dispatching & Updating Stock...' : 'Approve & Dispatch Stock to Agent'}
                            </button>
                          </div>
                        )}

                      </div>
                    );
                  });
                })()}
              </div>
            )}

          </div>
        </Modal>
      )}


      {/* Floating search button for mobile */}
      <div className="md:hidden">
          <button
            onClick={() => setFloatingSearchOpen(true)}
            className="fixed right-4 bottom-24 z-50 bg-indigo-600 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center"
            aria-label="Search products"
          >
            <FaSearch className="w-6 h-6" />
          </button>
      </div>
      
      {/* Search overlay for mobile */}
      {floatingSearchOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-start justify-center p-4">
            <div className="bg-white w-full max-w-sm rounded-lg shadow-lg">
                <div className="flex p-3 border-b">
                    <input
                        ref={floatingInputRef}
                        type="text"
                        placeholder="Search products..."
                        className="flex-1 pr-3 py-2 border-none focus:outline-none"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button onClick={() => setFloatingSearchOpen(false)} className="p-2 text-gray-600">
                        <FaTimes />
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* "Go to Top" button */}
      {showGoTop && (
        <button
          onClick={scrollToTop}
          aria-label="Go to top"
          style={{
            position: 'fixed',
            right: 16,
            bottom: goTopBottom,
            zIndex: 9999,
            width: 48,
            height: 48,
            borderRadius: 9999,
            background: '#1f2937',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
          }}
        >
          <FaArrowUp />
        </button>
      )}
    </div>
  );
};

export default InventoryDashboard;